import { useEffect } from 'react';

export function useNativeChannelMarkers({
    google,
    mapRef,
    tempChannels,
    nativeMarkersRef,
    nativeMarkerMap,
    markerDiscoveryRingsRef,
    markerTypes,
    pinSize,
    resolvePin,
    setActiveMarker,
    setSelectedPlace,
    setChatroomId,
    setShowingInfoWindow,
    setCenter,
    setIsEvents,
    handleCloseActiveEvent,
    onSelectPlace,
}) {
    // Collect native Google Maps marker instances for clustering + click handling.
    // Native markers ALWAYS start with map: null — the clusterer manages their visibility.
    // When searching, React markers take over display; native markers stay hidden.
    useEffect(() => {
        if (!google || !mapRef?.current?.map || !tempChannels?.length) {
            return;
        }

        // Clear old markers
        nativeMarkersRef.current.forEach((m) => m.setMap(null));
        nativeMarkersRef.current = [];
        nativeMarkerMap.current = {};

        // Clear old discovery rings
        markerDiscoveryRingsRef.current.forEach((r) => r.setMap(null));
        markerDiscoveryRingsRef.current = [];

        // Build native markers for each channel with an address
        tempChannels.forEach((channelData) => {
            if (!channelData?.addresses?.length) return;
            // Deduplicate addresses by lat/lng (prevents stacked flickering pins)
            const seen = new Set();
            channelData.addresses.forEach((addr) => {
                if (!addr?.latitude || !addr?.longitude) return;
                const key = `${addr.latitude.toFixed(6)},${addr.longitude.toFixed(6)}`;
                if (seen.has(key)) return;
                seen.add(key);

                const pin = resolvePin(channelData, markerTypes);
                const isDiscoveryHighlighted = channelData.discoverySource === 'philadelphia_targeted';
                const discoveryColor = '#d64545'; // red
                const discoveryLabel = isDiscoveryHighlighted
                    ? channelData.discoveryContext?.matchReason === 'corner_lot_and_park'
                        ? 'Corner Lot + Park'
                        : channelData.discoveryContext?.matchReason === 'corner_lot'
                          ? 'Corner Lot'
                          : channelData.discoveryContext?.matchReason === 'near_park'
                            ? 'Near Park'
                            : 'Targeted'
                    : null;
                const marker = new google.maps.Marker({
                    position: { lat: addr.latitude, lng: addr.longitude },
                    icon: {
                        url: pin.url,
                        scaledSize: new google.maps.Size(pinSize.width, pinSize.height),
                    },
                    map: null, // clusterer manages visibility
                    zIndex: isDiscoveryHighlighted ? 1000 : undefined,
                    ...(discoveryLabel
                        ? {
                              label: {
                                  text: discoveryLabel,
                                  color: '#d64545',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                              },
                          }
                        : {}),
                });

                // Highlight ring for targeted/visit-proximity discovery properties
                if (isDiscoveryHighlighted) {
                    const ring = new google.maps.Circle({
                        strokeColor: discoveryColor,
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: discoveryColor,
                        fillOpacity: 0.08,
                        map: null, // will be set when marker becomes visible
                        center: { lat: addr.latitude, lng: addr.longitude },
                        radius: 120, // ~400ft highlight ring
                    });
                    markerDiscoveryRingsRef.current.push(ring);
                }

                // Click handler — replicates onMarkerClick for native markers
                const props = {
                    id: channelData.id,
                    title: channelData.title,
                    name: channelData.subTitle,
                    position: { lat: addr.latitude, lng: addr.longitude },
                    teamData: channelData.teamData,
                    activeEvents: channelData.activeEvents,
                    accessibility: channelData.accessibility,
                };
                marker.addListener('click', () => {
                    window.setTimeout(() => {
                        setActiveMarker(marker);
                        setSelectedPlace(props);
                        setChatroomId(props?.id);
                        setShowingInfoWindow(true);
                        setCenter(props?.position);
                        setIsEvents(true);
                        handleCloseActiveEvent();
                        if (typeof onSelectPlace === 'function') {
                            try {
                                onSelectPlace(props);
                            } catch (_) {}
                        }
                    }, 100);
                });

                nativeMarkersRef.current.push(marker);
                nativeMarkerMap.current[channelData.id] = { marker, props };
            });
        });

        // Attach discovery rings to the map (after all markers are built)
        const mapInstance = mapRef.current?.map;
        if (mapInstance) {
            markerDiscoveryRingsRef.current.forEach((r) => r.setMap(mapInstance));
        }

        return () => {
            nativeMarkersRef.current.forEach((m) => m.setMap(null));
            nativeMarkersRef.current = [];
            nativeMarkerMap.current = {};
            markerDiscoveryRingsRef.current.forEach((r) => r.setMap(null));
            markerDiscoveryRingsRef.current = [];
        };
    }, [google, tempChannels]); // NOT searchActive — markers stay map:null regardless
}

export function useSearchFitBounds({ google, mapRef, tempChannels, isEvents, searchActive, setCenter }) {
    useEffect(() => {
        if (!google || !mapRef?.current?.map) return;
        if (!searchActive && isEvents) return; // event mode has its own fitBounds above
        if (!tempChannels || tempChannels.length < 1) return;

        const locations = tempChannels
            .flatMap((channel) =>
                (channel?.addresses || [])
                    .filter((address) => address?.latitude && address?.longitude)
                    .map((address) => ({ lat: address.latitude, lng: address.longitude }))
            )
            .filter((location) => location.lat && location.lng);

        if (locations.length === 0) return;

        if (locations.length === 1) {
            // Single result — center on it with a reasonable zoom
            setCenter({ lat: locations[0].lat, lng: locations[0].lng });
            mapRef.current.map.setCenter(new google.maps.LatLng(locations[0].lat, locations[0].lng));
            mapRef.current.map.setZoom(15);
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        locations.forEach((location) => bounds.extend(new google.maps.LatLng(location.lat, location.lng)));
        mapRef.current.map.fitBounds(bounds);
        // Cap zoom — don't zoom in too tight if all pins are clustered
        const listener = google.maps.event.addListenerOnce(mapRef.current.map, 'bounds_changed', () => {
            if (mapRef.current.map.getZoom() > 16) {
                mapRef.current.map.setZoom(16);
            }
        });
        return () => google.maps.event.removeListener(listener);
        // mapRef is stable and setCenter is a React state setter; avoid resetting bounds for their identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tempChannels, isEvents, searchActive, google]);
}
